import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const TABS = [
  { key:'visitors_daily',   label:'Daily Visitors',   icon:'📅' },
  { key:'visitors_monthly', label:'Monthly Visitors',  icon:'📆' },
  { key:'visitors_company', label:'By Company',        icon:'🏢' },
  { key:'audit',            label:'Audit Visitors',    icon:'🔍' },
  { key:'expenses',         label:'Expenses',          icon:'💰' },
  { key:'expenses_company', label:'Expense Summary',   icon:'📊' },
  { key:'couriers',         label:'Courier Report',    icon:'📦' },
  { key:'guest_rooms',      label:'Guest Rooms',       icon:'🏠' },
  { key:'employees',        label:'Employees',         icon:'👔' },
  { key:'meetings',         label:'Meetings',          icon:'📅' },
  { key:'employee_vehicles', label:'Employee Vehicles', icon:'🚗' },
];

const today = new Date().toISOString().split('T')[0];
const monthStart = new Date(new Date().setDate(1)).toISOString().split('T')[0];
const thisMonth = new Date().toISOString().substring(0, 7);

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [tab, setTab] = useState('visitors_daily');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filter state
  const [params, setParams] = useState({
    date: today, month: thisMonth,
    from: monthStart, to: today,
    company_id: '', department: '', unit_id: '',
    employee_id: '', category: '', status: '',
    guest_house_id: '', showroom_id: '',
    vehicle_model: '', vehicle_number: ''
  });

  // Reference data for filter dropdowns
  const [companies, setCompanies] = useState([]);
  const [units, setUnits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [guestHouses, setGuestHouses] = useState([]);
  const [showrooms, setShowrooms] = useState([]);

  useEffect(() => {
    if (!user) return;
    const loadRefs = async () => {
      if (isAdmin) {
        try { const r = await api.get('/api/companies'); setCompanies(r.data); } catch {}
      }
      try { const r = await api.get('/api/units'); setUnits(r.data); } catch {}
      try { const r = await api.get('/api/employees/active'); setEmployees(r.data); } catch {}
      try { const r = await api.get('/api/guest-houses'); setGuestHouses(r.data); } catch {}
      try { const r = await api.get('/api/showrooms/active'); setShowrooms(Array.isArray(r.data) ? r.data : []); } catch {}
    };
    loadRefs();
  }, [user?.role]);

  const buildQuery = () => {
    const p = { ...params };
    const q = [];
    if (p.date)          q.push(`date=${p.date}`);
    if (p.month)         q.push(`month=${p.month}`);
    if (p.from)          q.push(`from=${p.from}`);
    if (p.to)            q.push(`to=${p.to}`);
    if (p.company_id)    q.push(`company_id=${p.company_id}`);
    if (p.department)    q.push(`department=${encodeURIComponent(p.department)}`);
    if (p.unit_id)       q.push(`unit_id=${p.unit_id}`);
    if (p.employee_id)   q.push(`employee_id=${p.employee_id}`);
    if (p.category)      q.push(`category=${p.category}`);
    if (p.status)        q.push(`status=${p.status}`);
    if (p.guest_house_id)  q.push(`guest_house_id=${p.guest_house_id}`);
    if (p.showroom_id)     q.push(`showroom_id=${p.showroom_id}`);
    if (p.vehicle_model)   q.push(`vehicle_model=${encodeURIComponent(p.vehicle_model)}`);
    if (p.vehicle_number)  q.push(`vehicle_number=${encodeURIComponent(p.vehicle_number)}`);
    return q.join('&');
  };

  const load = async () => {
    setLoading(true); setData(null);
    try {
      const q = buildQuery();
      let res;
      if (tab === 'visitors_daily')   res = await api.get(`/api/reports/visitors/daily?${q}`);
      if (tab === 'visitors_monthly') res = await api.get(`/api/reports/visitors/monthly?${q}`);
      if (tab === 'visitors_company') res = await api.get(`/api/reports/visitors/by-company?${q}`);
      if (tab === 'audit')            res = await api.get(`/api/reports/visitors/audit?${q}`);
      if (tab === 'expenses')         res = await api.get(`/api/reports/expenses?${q}`);
      if (tab === 'expenses_company') res = await api.get(`/api/reports/expenses/by-company?${q}`);
      if (tab === 'couriers')         res = await api.get(`/api/reports/couriers?${q}`);
      if (tab === 'guest_rooms')      res = await api.get(`/api/reports/guest-rooms?${q}`);
      if (tab === 'employees')        res = await api.get(`/api/reports/employees?${q}`);
      if (tab === 'meetings')          res = await api.get(`/api/reports/meetings?${q}`);
      if (tab === 'employee_vehicles') res = await api.get(`/api/reports/employee-vehicles?${q}`);
      setData(res?.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]);

  const exportCSV = (rows, filename, colMap) => {
    if (!rows?.length) return;
    const keys = Object.keys(colMap);
    const header = Object.values(colMap).join(',') + '\n';
    const body = rows.map(r => keys.map(k => `"${r[k]||''}"`).join(',')).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = filename; a.click();
  };

  const setP = (key, val) => setParams(p => ({ ...p, [key]: val }));

  const renderFilters = () => (
    <div className="flex flex-wrap gap-3 items-end">
      {/* Date filters */}
      {tab === 'visitors_daily' && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
          <input type="date" value={params.date} onChange={e => setP('date', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
      )}
      {tab === 'visitors_monthly' && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Month</label>
          <input type="month" value={params.month} onChange={e => setP('month', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
      )}
      {!['visitors_daily','visitors_monthly'].includes(tab) && (
        <>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
            <input type="date" value={params.from} onChange={e => setP('from', e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
            <input type="date" value={params.to} onChange={e => setP('to', e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        </>
      )}

      {/* Company filter (super admin only) */}
      {isAdmin && companies.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Company</label>
          <select value={params.company_id} onChange={e => setP('company_id', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {/* Unit filter */}
      {!['employees','meetings'].includes(tab) && units.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Unit</label>
          <select value={params.unit_id} onChange={e => setP('unit_id', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All Units</option>
            {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      )}

      {/* Department filter */}
      {['visitors_daily','audit','expenses','employees'].includes(tab) && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Department</label>
          <input value={params.department} onChange={e => setP('department', e.target.value)}
            placeholder="e.g. IT, Finance"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-36" />
        </div>
      )}

      {/* Employee filter */}
      {['expenses'].includes(tab) && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Employee</label>
          <select value={params.employee_id} onChange={e => setP('employee_id', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All Employees</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
      )}

      {/* Expense category */}
      {tab === 'expenses' && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
          <select value={params.category} onChange={e => setP('category', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All</option>
            {['visitor','guest_house','employee','vehicle','courier','other'].map(c => (
              <option key={c} value={c} className="capitalize">{c}</option>
            ))}
          </select>
        </div>
      )}

      {/* Courier status */}
      {tab === 'couriers' && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
          <select value={params.status} onChange={e => setP('status', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All</option>
            {['pending','in_transit','delivered','returned'].map(s => (
              <option key={s} value={s} className="capitalize">{s}</option>
            ))}
          </select>
        </div>
      )}

      {/* Guest house */}
      {tab === 'guest_rooms' && guestHouses.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Guest House</label>
          <select value={params.guest_house_id} onChange={e => setP('guest_house_id', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All</option>
            {guestHouses.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </div>
      )}

      {/* Employee vehicle filters */}
      {tab === 'employee_vehicles' && (
        <>
          {showrooms.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Showroom</label>
              <select value={params.showroom_id} onChange={e => setP('showroom_id', e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All Showrooms</option>
                {showrooms.map(s => <option key={s.id} value={s.id}>{s.showroom_name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Vehicle Model</label>
            <input value={params.vehicle_model} onChange={e => setP('vehicle_model', e.target.value)}
              placeholder="e.g. Honda City"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-36" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Vehicle Number</label>
            <input value={params.vehicle_number} onChange={e => setP('vehicle_number', e.target.value)}
              placeholder="e.g. LZR-123"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-36" />
          </div>
          {employees.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Employee</label>
              <select value={params.employee_id} onChange={e => setP('employee_id', e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All Employees</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          )}
        </>
      )}

      <button onClick={load} className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
        🔍 Run Report
      </button>
    </div>
  );

  const renderTable = (rows, colMap, filename) => {
    if (!rows?.length) return (
      <div className="p-12 text-center text-gray-400">
        <div className="text-5xl mb-3">📊</div>
        <p>No data found for selected filters</p>
      </div>
    );
    const keys = Object.keys(colMap);
    return (
      <div>
        <div className="p-4 flex justify-between items-center border-b">
          <span className="text-sm text-gray-500">{rows.length} record(s)</span>
          <button onClick={() => exportCSV(rows, filename, colMap)}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm">
            📥 Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>{Object.values(colMap).map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  {keys.map(key => (
                    <td key={key} className="px-4 py-3 text-gray-700">
                      {(key.includes('_time') || key === 'in_time' || key === 'out_time')
                        ? (row[key] ? new Date(row[key]).toLocaleString('en-PK') : '—')
                        : key === 'amount' || key === 'total'
                        ? `PKR ${Number(row[key]||0).toLocaleString()}`
                        : (row[key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return (
      <div className="p-12 text-center text-gray-400">
        <div className="text-4xl animate-pulse mb-3">📊</div>
        <p>Generating report...</p>
      </div>
    );
    if (!data) return null;

    if (tab === 'visitors_daily') {
      return renderTable(data.visitors || [], {
        visitor_name:'Visitor', company_name:'Company', purpose:'Purpose',
        employee_name:'Employee', unit_name:'Unit', in_time:'In Time', out_time:'Out Time', badge_number:'Badge'
      }, 'daily-visitors.csv');
    }
    if (tab === 'visitors_monthly') {
      const rows = Array.isArray(data.daily) ? data.daily : [];
      const maxCount = Math.max(...rows.map(d => d.count), 1);
      return (
        <div>
          <div className="p-4 border-b flex justify-between items-center">
            <span className="font-semibold text-gray-700">Total: {data.total} visitors in {params.month}</span>
            <button onClick={() => exportCSV(rows, 'monthly-visitors.csv', { visit_date:'Date', count:'Visitors' })}
              className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm">📥 Export</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Visitors</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((d, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {new Date(d.visit_date).toLocaleDateString('en-PK', { weekday:'short', year:'numeric', month:'short', day:'numeric' })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="bg-blue-500 h-4 rounded" style={{ width: `${Math.max(4, (d.count / maxCount) * 150)}px` }} />
                        <span className="font-medium">{d.count}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    if (tab === 'visitors_company') {
      return renderTable(Array.isArray(data) ? data : [], {
        company_name:'Company', visitor_count:'Visitors'
      }, 'visitors-by-company.csv');
    }
    if (tab === 'audit') {
      return renderTable(Array.isArray(data) ? data : [], {
        visitor_name:'Visitor', company_name:'Company', cnic_passport:'CNIC',
        employee_name:'Employee', in_time:'In Time', out_time:'Out Time'
      }, 'audit-visitors.csv');
    }
    if (tab === 'expenses') {
      const rows = data.expenses || (Array.isArray(data) ? data : []);
      const total = typeof data.total === 'number' ? data.total : rows.reduce((s, r) => s + parseFloat(r.amount||0), 0);
      return (
        <div>
          <div className="p-4 border-b flex justify-between items-center">
            <span className="font-semibold">Total: PKR {Number(total).toLocaleString()} ({rows.length} entries)</span>
            <button onClick={() => exportCSV(rows, 'expenses.csv', {
              expense_date:'Date', expense_category:'Category', expense_type:'Description',
              company_name:'Company', employee_department:'Dept', employee_name:'Employee',
              amount:'Amount', remarks:'Remarks'
            })} className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm">📥 Export</button>
          </div>
          {renderTable(rows, {
            expense_date:'Date', expense_category:'Category', expense_type:'Description',
            company_name:'Company', employee_name:'Employee', amount:'Amount', remarks:'Remarks'
          }, 'expenses.csv')}
        </div>
      );
    }
    if (tab === 'expenses_company') {
      return renderTable(Array.isArray(data) ? data : [], {
        company_name:'Company', expense_category:'Category', total:'Total', count:'Count'
      }, 'expenses-by-company.csv');
    }
    if (tab === 'couriers') {
      return renderTable(Array.isArray(data) ? data : [], {
        courier_date:'Date', courier_company_name:'Courier Co', tracking_number:'Tracking',
        sender_employee_name:'Sender', sender_department:'Dept', company_name:'Company',
        receiver_city:'City', receiver_person:'Receiver', charges:'Charges', delivery_status:'Status'
      }, 'couriers.csv');
    }
    if (tab === 'guest_rooms') {
      return renderTable(Array.isArray(data) ? data : [], {
        check_in:'Check In', check_out:'Check Out', guest_name:'Guest', room_name:'Room',
        room_type:'Type', guest_house_name:'Guest House', company_name:'Company', nights:'Nights', status:'Status'
      }, 'guest-rooms.csv');
    }
    if (tab === 'employees') {
      return renderTable(Array.isArray(data) ? data : [], {
        employee_code:'Code', name:'Name', department:'Department', designation:'Designation',
        unit_name:'Unit', company_name:'Company', phone:'Phone', status:'Status'
      }, 'employees.csv');
    }
    if (tab === 'meetings') {
      return renderTable(Array.isArray(data) ? data : [], {
        meeting_date:'Date', title:'Title', room_name:'Room',
        host_name:'Host', start_time:'Start', end_time:'End', status:'Status'
      }, 'meetings.csv');
    }
    if (tab === 'employee_vehicles') {
      return renderTable(Array.isArray(data) ? data : [], {
        employee_code:'Emp Code', employee_name:'Employee', unit_name:'Unit',
        owner_name:'Owner', owner_cnic:'CNIC', owner_mobile:'Mobile',
        vehicle_number:'Vehicle #', vehicle_model:'Model',
        vehicle_chassis_number:'Chassis #', engine_number:'Engine #',
        hpa:'HPA', showroom_name:'Showroom', company_name:'Company',
        vehicle_purchase_date:'Purchase Date'
      }, 'employee-vehicles.csv');
    }
    return null;
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Reports</h1>
        <p className="text-gray-500 text-sm">Generate and export reports with company, department, and unit filters</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-blue-700 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        {renderFilters()}
      </div>

      {/* Report Output */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden min-h-48">
        {renderContent()}
      </div>
    </div>
  );
}
